import { useGetNNQuery, useListStreamsQuery } from "@/core/cameraApi";
import i18n from "@/core/i18n";
import { useCameraApiCredentials, useCameraApiUrl } from "@/hooks";
import { useAppSelector } from "@/hooks/redux";
import {
  selectActiveCameraMxid,
  selectActiveStreamName,
} from "@/modules/camera-selection/services/cameraSelectionSlice";
import type { NNDetections } from "@/modules/run/types/detections";
import type { RootState } from "@/store/types";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  CameraStreamContext,
  CameraStreamContextValue,
} from "../context/cameraStreamContext";
import { useLazyGetIceConfigQuery } from "@/modules/device/services/deviceApi";
import {
  decodeTimestampBurnin,
  stripDimensions,
} from "../utils/decodeTimestampBurnin";
import { isRelayCandidate, waitForIceGathering } from "../utils/iceGathering";
import { FrameMatcher, type SyncedFrame } from "../utils/frameMatcher";
import { watchForStall } from "../utils/streamStall";

/**
 * Used only when the control plane has no TURN configured. STUN alone does not
 * survive symmetric NAT or CGNAT, so this is a local-network fallback, not a
 * remote-access path.
 */
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: ["stun:stun.l.google.com:19302"] },
];

/**
 * Ceiling on ICE gathering. Higher than it used to be because a TURN allocation
 * is a full round trip on top of STUN, and signaling here is non-trickle: an
 * offer sent before the relay candidate exists can never acquire one later.
 */
const ICE_GATHERING_TIMEOUT_MS = 12000;
/** No video for this long and the connection is rebuilt, see watchForStall. */
const STREAM_STALL_TIMEOUT_MS = 5000;
/**
 * Backoff between rebuilds while they keep failing. The first one is
 * immediate; the cap keeps a camera that is gone for good at one attempt every
 * 30 s instead of a tight loop.
 */
const STREAM_RECONNECT_BASE_DELAY_MS = 1000;
const STREAM_RECONNECT_MAX_DELAY_MS = 30000;
const DETECTIONS_RECONNECT_DELAY_MS = 3000;
const DETECTIONS_MAX_RECONNECT_ATTEMPTS = 10;

export interface CameraStreamProviderProps {
  children: ReactNode;
}

/**
 * Owns a single WebRTC peer connection + a single detections WebSocket per
 * active project, keyed on the (mxid, streamName) tuple — the project's
 * camera (DB) plus the session's stream selection. Navigating between
 * Capture and Run for the same camera no longer rebuilds these — components
 * just attach to the shared streams via the context (see useWebRTCStream and
 * useSyncedRenderer).
 *
 * Mount this once inside the authenticated layout. The provider skips setup
 * until a project is active and a camera/stream is selected.
 *
 * A connection that fails, errors during the handshake or stops delivering
 * video is rebuilt on its own (reconnectNonce) — waking a laptop from sleep
 * otherwise left a black frame until the page was reloaded.
 */
export const CameraStreamProvider = ({ children }: CameraStreamProviderProps) => {
  const apiHost = useCameraApiUrl();
  const credentials = useCameraApiCredentials();

  const mxid = useAppSelector(selectActiveCameraMxid);
  const streamName = useAppSelector(selectActiveStreamName);

  // Bumped whenever the server restarts its pipeline for this stream
  // (deploy/stop NN, add/remove replay video, etc.) — see
  // cameraPipelineGenerationSlice. Used in the WebRTC useEffect deps so the
  // peer connection is rebuilt against the current pipeline, not the one it
  // was originally negotiated against.
  const pipelineGeneration = useAppSelector((state: RootState) =>
    mxid && streamName
      ? (state.cameraPipelineGeneration.byStream[`${mxid}-${streamName}`] ?? 0)
      : 0,
  );

  // Only run detections when an NN is actually deployed on this stream —
  // mirrors the behavior of the old useDetections hook (it gated on nnInfo).
  const { data: nnInfo } = useGetNNQuery(
    { mxid: mxid!, streamName: streamName! },
    { skip: !mxid || !streamName },
  );
  const hasNN = !!nnInfo;

  const { data: streams } = useListStreamsQuery(mxid!, { skip: !mxid });
  const isReplay =
    streams?.find((s) => s.name === streamName)?.replay === true;

  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [replayEnded, setReplayEnded] = useState(false);

  // Bumped to rebuild a dead connection; consecutive rebuilds that never get
  // video back count towards the backoff.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const reconnectAttemptsRef = useRef(0);

  const [detections, setDetections] = useState<NNDetections>({ detections: [] });
  const [isDetectionsConnected, setIsDetectionsConnected] = useState(false);
  const [detectionsError, setDetectionsError] = useState<string | null>(null);

  const subscribersRef = useRef<Set<(d: NNDetections) => void>>(new Set());
  const syncedSubscribersRef = useRef<Set<(s: SyncedFrame) => void>>(new Set());
  const matcherRef = useRef<FrameMatcher | null>(null);

  const subscribeDetections = useCallback(
    (cb: (d: NNDetections) => void) => {
      subscribersRef.current.add(cb);
      return () => {
        subscribersRef.current.delete(cb);
      };
    },
    [],
  );

  const subscribeSyncedFrames = useCallback(
    (cb: (s: SyncedFrame) => void) => {
      syncedSubscribersRef.current.add(cb);
      return () => {
        syncedSubscribersRef.current.delete(cb);
      };
    },
    [],
  );

  // TURN credentials are short-lived and minted per session, so they are
  // fetched when a connection is set up rather than held in module scope.
  const [fetchIceConfig] = useLazyGetIceConfigQuery();

  // A different stream starts without the backoff the previous one ran up.
  useEffect(() => {
    reconnectAttemptsRef.current = 0;
  }, [mxid, streamName, apiHost]);

  // WebRTC — one peer connection per (mxid, streamName, apiHost) tuple.
  useEffect(() => {
    if (!mxid || !streamName || !apiHost) {
      setMediaStream(null);
      setIsStreaming(false);
      setStreamError(null);
      return;
    }

    let cancelled = false;
    let peer: RTCPeerConnection | null = null;
    let stopStallWatch: (() => void) | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    const scheduleReconnect = () => {
      if (cancelled || reconnectTimeout) return;
      const attempt = reconnectAttemptsRef.current++;
      const delay =
        attempt === 0
          ? 0
          : Math.min(
              STREAM_RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1),
              STREAM_RECONNECT_MAX_DELAY_MS,
            );
      reconnectTimeout = setTimeout(
        () => setReconnectNonce((n) => n + 1),
        delay,
      );
    };

    const init = async () => {
      try {
        setStreamError(null);

        // A control plane with no TURN configured answers 503 here; that is a
        // STUN-only local-network setup, not an error worth failing on.
        const iceConfig = await fetchIceConfig(undefined, true)
          .unwrap()
          .catch(() => null);
        if (cancelled) return;

        const iceServers = iceConfig?.iceServers ?? FALLBACK_ICE_SERVERS;
        const expectRelay = iceServers.some((server) =>
          (Array.isArray(server.urls) ? server.urls : [server.urls]).some((url) =>
            url.startsWith("turn:") || url.startsWith("turns:"),
          ),
        );

        const pc = new RTCPeerConnection({
          iceServers,
          // Deliberately "all", not "relay": ICE already discards unroutable
          // pairs, so peer-to-peer is used wherever it works and TURN egress is
          // only paid at the sites that actually need it.
          iceTransportPolicy: "all",
        });
        peer = pc;
        setReplayEnded(false);
        let replayFinished = false;
        const eventsChannel = pc.createDataChannel("events");
        eventsChannel.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data as string);
            if (msg.event === "eof") {
              replayFinished = true;
              setReplayEnded(true);
            }
          } catch {
            // ignore malformed messages
          }
        };

        stopStallWatch = watchForStall(pc, {
          timeoutMs: STREAM_STALL_TIMEOUT_MS,
          isIdle: () => replayFinished,
          onProgress: () => {
            reconnectAttemptsRef.current = 0;
          },
          onStall: () => {
            setIsStreaming(false);
            scheduleReconnect();
          },
        });
        pc.addTransceiver("video", { direction: "recvonly" });

        pc.addEventListener("track", (event) => {
          if (cancelled) return;
          const incoming = event.streams[0] ?? null;
          setMediaStream(incoming);
          setIsStreaming(true);
        });

        const iceCandidates: RTCIceCandidate[] = [];
        pc.addEventListener("icecandidate", (event) => {
          if (event.candidate) iceCandidates.push(event.candidate);
        });

        pc.addEventListener("connectionstatechange", () => {
          if (cancelled) return;
          if (
            pc.connectionState === "failed" ||
            pc.connectionState === "disconnected"
          ) {
            setIsStreaming(false);
            setStreamError(i18n.t("camera:stream.errors.connectionLost"));
            // "disconnected" can still recover by itself; the stall watch
            // rebuilds it if it doesn't. "failed" never does.
            if (pc.connectionState === "failed") scheduleReconnect();
          } else if (pc.connectionState === "connected") {
            setIsStreaming(true);
            setStreamError(null);
          }
        });

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        // Signaling is non-trickle — the candidates embedded in this offer are
        // the only ones the connection will ever have — so waiting for a relay
        // candidate matters more than answering quickly.
        await waitForIceGathering(
          pc,
          () => iceCandidates.some(isRelayCandidate),
          { timeoutMs: ICE_GATHERING_TIMEOUT_MS, expectRelay },
        );

        if (cancelled) return;

        // localDescription, NOT the object createOffer() returned.
        //
        // createOffer() produces a snapshot taken before ICE gathering, so its
        // sdp carries no a=candidate lines at all. Only localDescription is
        // updated as candidates arrive. Signaling here is non-trickle, so
        // posting the snapshot left the camera with ZERO remote candidates: it
        // had nothing to send connectivity checks to, every pair failed with no
        // bytes exchanged, and the browser saw a grey frame and then
        // "Connection lost" — while the camera's own candidates came back fine,
        // because the device correctly answers with its localDescription.
        const offerSdp = pc.localDescription?.sdp ?? offer.sdp;

        const offerUrl = `${apiHost}/cameras/${mxid}/streams/${streamName}/video`;
        const response = await fetch(offerUrl, {
          method: "POST",
          // The relay authorizes by a cookie on its own origin, and a raw fetch
          // has to opt in or the SDP offer arrives unauthenticated and the edge
          // answers 401. Only on the relay path, though: a LAN camera refuses a
          // credentialed request at the preflight, so this must not be
          // hardcoded to "include".
          credentials,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sdp: offerSdp,
            type: offer.type,
            candidates: iceCandidates.map((c) => ({
              candidate: c.candidate,
              sdpMLineIndex: c.sdpMLineIndex,
              sdpMid: c.sdpMid,
            })),
          }),
        });
        if (!response.ok) {
          throw new Error(
            i18n.t("camera:stream.errors.answerFailed", {
              status: response.statusText,
            }),
          );
        }
        const answerData = await response.json();
        if (cancelled) return;
        await pc.setRemoteDescription(new RTCSessionDescription(answerData));

        if (Array.isArray(answerData.candidates)) {
          for (const candidate of answerData.candidates) {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(candidate));
            } catch (err) {
              console.warn("Failed to add ICE candidate:", err);
            }
          }
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : i18n.t("camera:stream.errors.unknown");
        setStreamError(message);
        setIsStreaming(false);
        console.error("WebRTC error:", err);
        scheduleReconnect();
      }
    };

    init();

    return () => {
      cancelled = true;
      stopStallWatch?.();
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (peer) {
        peer.close();
      }
      setMediaStream(null);
      setIsStreaming(false);
    };
  }, [
    mxid,
    streamName,
    apiHost,
    credentials,
    pipelineGeneration,
    reconnectNonce,
    fetchIceConfig,
  ]);

  // Matcher lifecycle. Resets whenever NN deploy state or pipeline topology
  // changes, since pending frame/detection ts values from a previous
  // session aren't meaningful anymore. The matcher's emit is fanned out to
  // syncedSubscribersRef so subscribers survive matcher rebuilds.
  useEffect(() => {
    if (!hasNN) return;

    const matcher = new FrameMatcher();
    matcher.onMatch((synced) => {
      if (syncedSubscribersRef.current.size === 0) {
        // No live subscriber: matcher's emit transferred ownership to us
        // and there's nobody to hand it off to — close to avoid leaks.
        synced.bitmap.close();
        return;
      }
      for (const cb of syncedSubscribersRef.current) {
        try {
          cb(synced);
        } catch (e) {
          console.error("Synced frame subscriber threw:", e);
        }
      }
    });
    matcherRef.current = matcher;

    return () => {
      matcherRef.current = null;
      matcher.dispose();
    };
  }, [hasNN, mxid, streamName, pipelineGeneration]);

  // Frame capture loop — drives the matcher's frame side. Only runs when
  // both a media stream and NN are present; otherwise there's nothing to
  // sync.
  useEffect(() => {
    if (!mediaStream || !hasNN) return;

    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.srcObject = mediaStream;
    void video.play().catch(() => {});

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    let cancelled = false;
    let rvfcId: number | undefined;

    const onFrame: VideoFrameRequestCallback = () => {
      if (cancelled) return;
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (w === 0 || h === 0) {
        rvfcId = video.requestVideoFrameCallback(onFrame);
        return;
      }

      const { stripWidth, stripHeight } = stripDimensions(w);
      if (stripWidth > w || stripHeight > h) {
        rvfcId = video.requestVideoFrameCallback(onFrame);
        return;
      }

      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }

      ctx.drawImage(video, 0, 0);
      const stripData = ctx.getImageData(0, 0, stripWidth, stripHeight);
      const ts = decodeTimestampBurnin(stripData, w);
      const capturedAt = performance.now();

      // Snapshot the canvas before re-arming so the bitmap captures THIS
      // frame even if the next callback overwrites the canvas before our
      // promise resolves.
      const bitmapPromise =
        ts !== null && matcherRef.current
          ? createImageBitmap(canvas)
          : null;

      rvfcId = video.requestVideoFrameCallback(onFrame);

      if (bitmapPromise) {
        bitmapPromise.then(
          (bitmap) => {
            if (cancelled || !matcherRef.current) {
              bitmap.close();
              return;
            }
            matcherRef.current.pushFrame(ts!, bitmap, capturedAt);
          },
          (err) => {
            console.error("createImageBitmap failed:", err);
          },
        );
      }
    };

    rvfcId = video.requestVideoFrameCallback(onFrame);

    return () => {
      cancelled = true;
      if (rvfcId !== undefined) {
        video.cancelVideoFrameCallback(rvfcId);
      }
      video.srcObject = null;
      video.pause();
    };
  }, [mediaStream, hasNN]);

  // Detections WebSocket — only when an NN is deployed on this stream.
  //
  // Rebuilt along with the video (reconnectNonce). A sleep that killed the
  // peer connection most likely killed this socket on the device side too,
  // and a socket that only receives has nothing of its own to notice that
  // with — while it sits half-open, the synced renderer has frames but no
  // detections to match them to.
  useEffect(() => {
    if (!mxid || !streamName || !apiHost || !hasNN) {
      setDetections({ detections: [] });
      setIsDetectionsConnected(false);
      setDetectionsError(null);
      return;
    }

    let cancelled = false;
    let ws: WebSocket | null = null;
    let reconnectAttempts = 0;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (cancelled) return;
      const wsUrl = apiHost
        .replace(/^https:\/\//, "wss://")
        .replace(/^http:\/\//, "ws://");
      const endpoint = `${wsUrl}/cameras/${mxid}/streams/${streamName}/nn`;

      try {
        ws = new WebSocket(endpoint);

        ws.onopen = () => {
          if (cancelled) return;
          setIsDetectionsConnected(true);
          setDetectionsError(null);
          reconnectAttempts = 0;
        };

        ws.onmessage = async (event) => {
          if (cancelled) return;
          try {
            const data = JSON.parse(event.data);
            const parsed: NNDetections = Array.isArray(data)
              ? { detections: data }
              : data;
            // Decode the compact PNG mask (if present) into an ImageBitmap
            // before publishing, so the synchronous renderer can drawImage
            // without blocking on async decode each paint. Single-channel
            // uint8 PNG where pixel value 0 means background and N means
            // detections[N - 1].
            if (parsed.masks_png) {
              try {
                const raw = atob(parsed.masks_png);
                const buf = new Uint8Array(raw.length);
                for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
                const blob = new Blob([buf], { type: "image/png" });
                parsed.maskBitmap = await createImageBitmap(blob);
              } catch {
                // Decode failures fall through to the legacy masks array
                // path on the renderer side, if the server sent one.
              }
              if (cancelled) {
                parsed.maskBitmap?.close?.();
                return;
              }
            }
            setDetections((prev) => {
              prev.maskBitmap?.close?.();
              return parsed;
            });
            subscribersRef.current.forEach((cb) => cb(parsed));
            // Push to matcher for synced rendering. Clone the maskBitmap
            // so the matcher owns its own copy independent of the legacy
            // setDetections cleanup; otherwise either side closing it
            // crashes the other.
            if (parsed.ts_us !== undefined && matcherRef.current) {
              const ts32 = parsed.ts_us % 0x100000000;
              let detectionsForMatcher: NNDetections = parsed;
              if (parsed.maskBitmap) {
                try {
                  const clonedMask = await createImageBitmap(parsed.maskBitmap);
                  detectionsForMatcher = { ...parsed, maskBitmap: clonedMask };
                } catch {
                  // If cloning fails, fall through with no mask in the
                  // matcher's copy rather than risking a double-close.
                  const { maskBitmap: _drop, ...rest } = parsed;
                  detectionsForMatcher = rest as NNDetections;
                }
              }
              matcherRef.current.pushDetections(
                ts32,
                detectionsForMatcher,
                performance.now(),
              );
            }
          } catch {
            // ignore malformed messages
          }
        };

        ws.onerror = () => {
          if (cancelled) return;
          setDetectionsError(i18n.t("camera:stream.errors.detectionsSocket"));
        };

        ws.onclose = () => {
          if (cancelled) return;
          setIsDetectionsConnected(false);
          ws = null;
          if (reconnectAttempts < DETECTIONS_MAX_RECONNECT_ATTEMPTS) {
            reconnectAttempts += 1;
            reconnectTimeout = setTimeout(
              connect,
              DETECTIONS_RECONNECT_DELAY_MS,
            );
          } else {
            setDetectionsError(
              i18n.t("camera:stream.errors.detectionsMaxReconnects"),
            );
          }
        };
      } catch (err) {
        setDetectionsError(
          err instanceof Error
            ? err.message
            : i18n.t("camera:stream.errors.detectionsConnectFailed"),
        );
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (ws) {
        ws.close();
      }
      setDetections({ detections: [] });
      setIsDetectionsConnected(false);
    };
  }, [mxid, streamName, apiHost, hasNN, reconnectNonce]);

  const value: CameraStreamContextValue = {
    mediaStream,
    isStreaming,
    streamError,
    replayEnded,
    isReplay,
    detections,
    isDetectionsConnected,
    detectionsError,
    subscribeDetections,
    subscribeSyncedFrames,
  };

  return (
    <CameraStreamContext.Provider value={value}>
      {children}
    </CameraStreamContext.Provider>
  );
};
